import cx from "classnames";
import {
  KeenSliderHooks,
  KeenSliderInstance,
  useKeenSlider,
} from "keen-slider/react";
import { useTranslation } from "next-i18next";
import React, { ReactNode, useEffect, useState } from "react";

import { Icon } from "@/components/elements/Icon";

import styles from "./styles.module.css";

export type CarouselProps = {
  slides: ReactNode[];
  carouselConfig?: Record<string, unknown>;
  slideClassName?: string;
  buttonContainerClassName?: string;
  className?: string;
};

const WheelControls = (slider) => {
  let touchTimeout;
  let position;
  let wheelActive;

  function dispatch(e, name) {
    position.x -= e.deltaX;
    position.y -= e.deltaY;
    slider.container.dispatchEvent(
      new CustomEvent(name, {
        detail: {
          x: position.x,
          y: position.y,
        },
      })
    );
  }

  function wheelStart(e) {
    position = {
      x: e.pageX,
      y: e.pageY,
    };
    dispatch(e, "ksDragStart");
  }

  function wheel(e) {
    dispatch(e, "ksDrag");
  }

  function wheelEnd(e) {
    dispatch(e, "ksDragEnd");
  }

  function eventWheel(e) {
    if (!e.deltaX) return;
    e.preventDefault();
    if (!wheelActive) {
      wheelStart(e);
      wheelActive = true;
    }
    wheel(e);
    clearTimeout(touchTimeout);
    touchTimeout = setTimeout(() => {
      wheelActive = false;
      wheelEnd(e);
    }, 50);
  }

  slider.on("created", () => {
    slider.container.addEventListener("wheel", eventWheel, {
      passive: false,
    });
  });
};

const testSlideVisibility = (
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  slider: KeenSliderInstance<{}, {}, KeenSliderHooks>,
  slideIndex: number
): boolean => {
  if (!slider) return false;
  const slides = slider.track.details?.slides;
  return Math.round(slides[slideIndex].portion) === 1;
};

export const Carousel = ({
  slides,
  carouselConfig,
  slideClassName,
  buttonContainerClassName,
  className,
}: CarouselProps) => {
  const { t } = useTranslation();
  // States for slider
  const [showPrevArrow, setShowPrevArrow] = useState(false);
  const [showNextArrow, setShowNextArrow] = useState(true);
  const [updated, setUpdated] = useState(false);
  const update = () => setUpdated(true);
  const [allSlidesVisible, setAllSlidesVisible] = useState(false);

  // CHECK IF ALL SLIDES ARE VISBLE
  const testAllSlidesAreVisible = (
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    slider: KeenSliderInstance<{}, {}, KeenSliderHooks>
  ) => {
    const firstSlideVisible = testSlideVisibility(slider, 0);
    const lastSlideVisible = testSlideVisibility(slider, slides.length - 1);
    if (firstSlideVisible && lastSlideVisible) {
      setAllSlidesVisible(true);
      return;
    }
    setAllSlidesVisible(false);
  };
  useEffect(() => {
    if (!updated) return;
    setTimeout(() => {
      testAllSlidesAreVisible(slider.current);
    }, 300);
    setUpdated(false);
  }, [updated]);
  // SETUP SLIDER
  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>(
    {
      ...carouselConfig,
      loop: false,
      initial: 0,
      animationEnded(s) {
        const currentSlide = s.track.details.rel;
        const isLastSlideVisible = testSlideVisibility(s, slides.length - 1);
        const showNext =
          !isLastSlideVisible && currentSlide < slides.length - 1;
        setShowNextArrow(showNext);
        const showPrev = currentSlide > 0;
        setShowPrevArrow(showPrev);
      },
      created: update,
      updated: update,
    },
    [WheelControls]
  );

  return (
    <div
      ref={sliderRef}
      className={cx("keen-slider", styles.container, className)}
    >
      {slides.map((slide, index) => (
        <div className={cx("keen-slider__slide", slideClassName)} key={index}>
          {slide}
        </div>
      ))}
      {!allSlidesVisible && (
        <div className={cx(buttonContainerClassName, styles.controls)}>
          <button
            aria-label={t("previous_CTA")}
            className={cx({
              [styles.hidden]: !showPrevArrow,
            })}
            onClick={() => {
              if (slider.current) slider.current.prev();
            }}
          >
            <Icon type={"chevron_left"} />
          </button>

          <button
            aria-label={t("next_CTA")}
            className={cx({
              [styles.hidden]: !showNextArrow,
            })}
            onClick={() => {
              if (slider.current) slider.current.next();
            }}
          >
            <Icon type="chevron_right" />
          </button>
        </div>
      )}
    </div>
  );
};
